/* eslint-disable react-hooks/exhaustive-deps */
import React, { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { InputAdornment, ListItemIcon, ListItemText } from '@material-ui/core'
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  TextField,
  styled,
} from '@mui/material'
import { colors as bsColors } from '@resiliatech/birdsong'
import { ChecklistItemWithResourceInfo } from 'TypeDefs/Goal'
import { Search } from '@material-ui/icons'
import { colors } from 'Themes/Colors'
import { WatchQueryFetchPolicy, useLazyQuery } from '@apollo/client'
import GET_ACADEMY_RESOURCES from 'ApolloAcademy/Queries/GetAcademyResources'
import {
  Resource,
  ResourceSortOptions,
  ResourceType,
} from 'Generated/academy-graphql-types'
import { GatedChip, ResourceChip } from 'Components/Pages/Academy/_common/Chips'
import { ACADEMY_CONTEXT } from 'Components/Pages/Academy/_common/utils'
import { debounce } from 'lodash'

const MINIMUM_QUERY_LENGTH_FOR_SEARCH = 3

const FETCH_POLICY_NO_CACHE = {
  fetchPolicy: 'no-cache' as WatchQueryFetchPolicy,
}

type AcademyResourceSearchProps = {
  label?: string
  placeholder?: string
  onSelectResource: (
    resourceInfo: ChecklistItemWithResourceInfo['resource']
  ) => void
  resource?: Resource | null
}

export const AcademyResourceSearch = ({
  label,
  placeholder,
  onSelectResource,
  resource,
}: AcademyResourceSearchProps) => {
  const [query, setQuery] = useState<string>('')
  const [resourceAutoCompleteList, setResourceAutoCompleteList] = useState<
    ChecklistItemWithResourceInfo['resource'][]
  >([])

  const [getResources, { data, loading }] = useLazyQuery(
    GET_ACADEMY_RESOURCES,
    {
      ...ACADEMY_CONTEXT,
      ...FETCH_POLICY_NO_CACHE,
      variables: {
        resourceType: [
          ResourceType.Article,
          ResourceType.Course,
          ResourceType.Template,
          ResourceType.LearningTrack,
        ],
        query: '',
        sort: ResourceSortOptions.Random,
        anonymousId: analytics?.user?.()?.anonymousId?.(),
      },
      onCompleted: () => {
        setResourceAutoCompleteList(data?.resources?.nodes ?? [])
      },
    }
  )

  const debounceSetQuery = useCallback(debounce(setQuery, 500), [])

  const handleInputChange = useCallback(
    async (searchTerm: string) => {
      if (searchTerm.length >= MINIMUM_QUERY_LENGTH_FOR_SEARCH) {
        await getResources({
          variables: {
            resourceType: [
              ResourceType.Article,
              ResourceType.Course,
              ResourceType.Template,
              ResourceType.LearningTrack,
            ],
            query: searchTerm || '',
            sort: ResourceSortOptions.Alphabetical,
            anonymousId: analytics?.user?.()?.anonymousId?.(),
          },
        })
      }
    },
    [getResources]
  )

  // Update resource list when data changes
  useEffect(() => {
    if (data?.resources?.nodes) {
      setResourceAutoCompleteList(data.resources.nodes)
    }
  }, [data])

  const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: Resource | undefined | null
  ) => {
    if (!option) {
      return null
    }
    const { title, gatedByPlan } = option
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <li {...props}>
        <ListItemText primary={title} />
        <ListItemIcon style={{ padding: '10px' }}>
          <ResourceChip resource={option} />
        </ListItemIcon>
        <ListItemIcon>{gatedByPlan && <GatedChip />}</ListItemIcon>
      </li>
    )
  }

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <OutlinedTextInput
      data-testid='task-list-item-academy-resource-search-input'
      id={params.id}
      value={query}
      onChange={({ target: { value } }) => {
        debounceSetQuery(value)
        // Trigger search when user types
        if (value.length >= MINIMUM_QUERY_LENGTH_FOR_SEARCH) {
          handleInputChange(value)
        }
      }}
      variant='outlined'
      label={label || 'Link to an Academy Resource'}
      placeholder={
        placeholder || 'Start typing to search for Academy resources'
      }
      InputProps={{
        startAdornment: (
          <InputAdornment position='start' id='startAdornment'>
            <SearchIcon />
          </InputAdornment>
        ),
      }}
      inputProps={params.inputProps}
      ref={params.InputProps.ref}
    />
  )

  return (
    <Autocomplete
      // disabling the built-in filtering of the Autocomplete component by overriding the filterOptions prop
      // found in the Autocomplete API documentation: https://mui.com/material-ui/react-autocomplete/
      filterOptions={(x) => x}
      loading={loading}
      loadingText='Searching for Academy Resources...'
      data-testid='task-list-item-academy-resource-search'
      onChange={(event, value) => {
        onSelectResource(value)
      }}
      renderOption={renderOption}
      options={resourceAutoCompleteList}
      getOptionLabel={(option) => option?.title ?? ''}
      clearOnEscape={false}
      renderInput={renderInput}
      value={resource}
    />
  )
}

export const OutlinedTextInput = styled(TextField)`
  width: 100%;

  & .MuiOutlinedInput-root {
    & fieldset {
      border-color: ${colors.resiliaBlue};
    }

    &:hover fieldset {
      border-color: ${colors.resiliaBlue};
    }

    &.Mui-focused fieldset {
      border-color: ${colors.resiliaBlue};
    }
  }

  & .MuiFormLabel-root {
    color: ${bsColors.text.secondary};
  }
`

export const SearchIcon = styled(Search)`
  color: ${colors.resiliaBlue};
  font-size: 24px;
`
